// One audio-thread clock for microphone-only capture and Guide call mixing.
// No chained main-thread timers: hidden tabs must keep producing 50 ms chunks.
import { STREAMING_SAMPLE_RATE } from "./universalStreamingConfig";

export const CAPTURE_PROCESSOR_NAME = "assembly-audio-capture";
export const CAPTURE_CHUNK_MS = 50;
const registeredContexts = new WeakMap();
// How long start waits for a new context to run before handing it back paused.
export const START_RESUME_WAIT_MS = 1000;
const MIC_INPUT = 0;
const CALL_INPUT = 1;

/**
 * The processor, as source text: registered from a Blob URL so it ships in
 * this module instead of as a separate public asset. It resamples by
 * integrate-and-dump: each output sample is the mean of the input that falls
 * inside its step (at 48 kHz, three samples), with a sample that straddles
 * two steps split between them and the running sum carried across render
 * quanta. That mean is a low-pass, so sibilants, clicks and music above the
 * target rate's band are damped instead of folding into the speech band as
 * they do when every third sample is simply picked (the SDK's
 * pcm16EncoderWorkletSource). Both inputs share one position, so the mic and
 * the call stay sample-aligned. A missing input (an ended track, or no call
 * stream) is silence: time keeps moving and chunks keep coming.
 * `sampleRate`, `AudioWorkletProcessor` and `registerProcessor` are the
 * worklet scope's globals.
 */
export const CAPTURE_WORKLET_SOURCE = `
const DEFAULT_QUANTUM = 128;

// One input as mono: its only channel, the mean of several, or null (silence)
// when it has none.
function toMono(input, scratch) {
  if (!input || input.length === 0) return null;
  if (input.length === 1) return input[0];
  const length = input[0].length;
  for (let i = 0; i < length; i++) {
    let sum = 0;
    for (let c = 0; c < input.length; c++) sum += input[c][i];
    scratch[i] = sum / input.length;
  }
  return scratch;
}

function rms(sumOfSquares, count) {
  const level = Math.sqrt(sumOfSquares / count);
  return level > 1 ? 1 : level;
}

class AudioCaptureProcessor extends AudioWorkletProcessor {
  constructor(options) {
    super();
    const opts = (options && options.processorOptions) || {};
    this.targetRate = opts.targetRate || 16000;
    this.chunkSize = Math.round((this.targetRate * (opts.chunkMs || 50)) / 1000);
    this.hasCall = Boolean(opts.hasCall);
    // Input samples per output sample, and how much input the output being
    // built still needs (fractional when the rates don't divide).
    this.step = sampleRate / this.targetRate;
    this.needed = this.step;
    this.micSum = 0;
    this.callSum = 0;
    this.samples = 0;
    this.scratch = [new Float32Array(DEFAULT_QUANTUM), new Float32Array(DEFAULT_QUANTUM)];
    this.startChunk();
    this.port.onmessage = ({ data }) => {
      if (data?.type !== "flush") return;
      this.finished = data.finish !== false;
      // Preserve the trailing word; pad to the provider's minimum 50 ms frame.
      if (this.filled) this.flush();
      this.port.postMessage({ type: "flushed" });
    };
  }

  startChunk() {
    this.mic = new Float32Array(this.chunkSize);
    this.call = this.hasCall ? new Float32Array(this.chunkSize) : null;
    this.filled = 0;
  }

  quantumLength(inputs) {
    for (const input of inputs) {
      if (input && input.length > 0) return input[0].length;
    }
    return typeof renderQuantumSize === "number" ? renderQuantumSize : DEFAULT_QUANTUM;
  }

  process(inputs) {
    if (this.finished) return false;
    const length = this.quantumLength(inputs);
    if (this.scratch[0].length < length) {
      this.scratch = [new Float32Array(length), new Float32Array(length)];
    }
    const mic = toMono(inputs[${MIC_INPUT}], this.scratch[0]);
    const call = toMono(inputs[${CALL_INPUT}], this.scratch[1]);
    for (let i = 0; i < length; i++) {
      const m = mic ? mic[i] : 0;
      const c = call ? call[i] : 0;
      // This input sample's weight still to hand out: all of it to the
      // current step, or the part that fits and the rest to the next.
      let left = 1;
      while (left > 0) {
        const take = left < this.needed ? left : this.needed;
        this.micSum += m * take;
        this.callSum += c * take;
        left -= take;
        this.needed -= take;
        if (this.needed > 0) break;
        this.emit();
      }
    }
    return true;
  }

  // One output sample: the mean over its step.
  emit() {
    this.mic[this.filled] = this.micSum / this.step;
    if (this.call) this.call[this.filled] = this.callSum / this.step;
    this.micSum = 0;
    this.callSum = 0;
    this.needed = this.step;
    this.filled += 1;
    if (this.filled === this.chunkSize) this.flush();
  }

  flush() {
    const size = this.chunkSize;
    const pcm = new Int16Array(size);
    let micSquares = 0;
    let callSquares = 0;
    for (let k = 0; k < size; k++) {
      const m = this.mic[k];
      const c = this.call ? this.call[k] : 0;
      micSquares += m * m;
      callSquares += c * c;
      const sum = m + c;
      const clamped = sum < -1 ? -1 : sum > 1 ? 1 : sum;
      pcm[k] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
    }
    this.samples += size;
    const transfer = [pcm.buffer, this.mic.buffer];
    if (this.call) transfer.push(this.call.buffer);
    this.port.postMessage(
      {
        pcm: pcm.buffer,
        mic: this.mic,
        call: this.call,
        micLevel: rms(micSquares, size),
        callLevel: rms(callSquares, size),
        samples: this.samples,
      },
      transfer
    );
    this.startChunk();
  }
}

registerProcessor("${CAPTURE_PROCESSOR_NAME}", AudioCaptureProcessor);
`;

function registerCaptureProcessor(context) {
  if (!registeredContexts.has(context)) {
    const url = URL.createObjectURL(new Blob([CAPTURE_WORKLET_SOURCE], { type: "application/javascript" }));
    const registered = context.audioWorklet.addModule(url)
      .catch(error => { registeredContexts.delete(context); throw error; })
      .finally(() => URL.revokeObjectURL(url));
    registeredContexts.set(context, registered);
  }
  return registeredContexts.get(context);
}

function disconnect(node) {
  try {
    node?.disconnect();
  } catch {
    // Already disconnected.
  }
}

/**
 * Capture caller-owned streams. An optional existing audio context stays open
 * on stop (the simulator's media-element source cannot move to a new context).
 * recordingSource joins only the recording mix, never the transcriber.
 * stop({ flush: true }) delivers the worklet's final frame before disconnecting.
 */
export async function startAudioCapture({
  micStream,
  callStream = null,
  onChunk,
  onStateChange,
  targetSampleRate = STREAMING_SAMPLE_RATE,
  audioContext = null,
  recordingSource = null,
}) {
  if ((!audioContext && typeof AudioContext === "undefined") || typeof AudioWorkletNode === "undefined") {
    throw Object.assign(new Error("This browser cannot capture live audio. Try an updated browser."), { name: "NotSupportedError" });
  }

  const context = audioContext || new AudioContext();
  let capture;
  try {
    await registerCaptureProcessor(context);
    capture = buildGraph(context, { micStream, callStream, onChunk, targetSampleRate, recordingSource, ownsContext: !audioContext });
  } catch (error) {
    if (!audioContext) context.close().catch(() => {});
    throw error;
  }
  context.onstatechange = () => {
    const { state } = context;
    onStateChange?.(state);
    if (isPaused(state)) context.resume().catch(() => {});
  };
  await withinMs(capture.resume().catch(() => {}), START_RESUME_WAIT_MS);
  return capture;
}

function isPaused(state) {
  return state === "suspended" || state === "interrupted";
}

/** Waits for `promise`, but no longer than `ms`. */
function withinMs(promise, ms) {
  let timer;
  const expired = new Promise((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([promise, expired]).finally(() => clearTimeout(timer));
}

function buildGraph(context, { micStream, callStream, onChunk, targetSampleRate, recordingSource, ownsContext }) {
  const node = new AudioWorkletNode(context, CAPTURE_PROCESSOR_NAME, {
    numberOfInputs: 2,
    numberOfOutputs: 0,
    channelCount: 1,
    channelCountMode: "explicit",
    channelInterpretation: "speakers",
    processorOptions: {
      targetRate: targetSampleRate,
      chunkMs: CAPTURE_CHUNK_MS,
      hasCall: Boolean(callStream),
    },
  });
  // The recording is mono: a stereo destination would store the one mix twice.
  const destination = context.createMediaStreamDestination();
  destination.channelCount = 1;
  destination.channelCountMode = "explicit";

  const connectSource = (stream, input) => {
    const source = context.createMediaStreamSource(stream);
    source.connect(node, 0, input);
    source.connect(destination);
    return source;
  };
  let micSource = connectSource(micStream, MIC_INPUT);
  const callSource = callStream ? connectSource(callStream, CALL_INPUT) : null;
  recordingSource?.connect(destination);
  let acknowledgeFlush;
  node.port.onmessage = ({ data }) => {
    if (data.type === "flushed") acknowledgeFlush?.();
    else onChunk?.(data);
  };

  let stopped = null;
  let flushing = null;
  function flush(finish = false) {
    if (flushing) return flushing;
    if (context.state !== "running") return Promise.resolve();
    const flushed = new Promise(resolve => { acknowledgeFlush = resolve; });
    node.port.postMessage({ type: "flush", finish });
    flushing = withinMs(flushed, START_RESUME_WAIT_MS).finally(() => { flushing = null; });
    return flushing;
  }
  return {
    mixedStream: destination.stream,
    flush: () => stopped || flush(),

    get state() {
      return context.state;
    },

    replaceMic(stream) {
      if (stopped) return;
      // Connect the new mic before letting go of the old one: no gap.
      const next = connectSource(stream, MIC_INPUT);
      disconnect(micSource);
      micSource = next;
    },

    resume() {
      return !stopped && isPaused(context.state) ? context.resume() : Promise.resolve();
    },

    stop({ flush: flushTail = false } = {}) {
      if (!stopped) {
        stopped = (async () => {
          if (flushTail) await flush(true);
          context.onstatechange = null;
          node.port.onmessage = null;
          node.port.close?.();
          [micSource, callSource, node, destination].forEach(disconnect);
          try { recordingSource?.disconnect(destination); } catch { /* Already disconnected. */ }
          if (ownsContext && context.state !== "closed") await context.close().catch(() => {});
        })();
      }
      return stopped;
    },
  };
}
